import { ECustomerPaymentType } from "../../Enum/ECustomerPaymentType"
import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import { EPaymentType } from "../../Enum/EPaymentType"
import { OrderEntity } from "../Order/OrderEntity"
import { orderMemory } from "../Memory/Memory"
import { employeeMemory } from "../Memory/Memory"
import { IEmployeePerformanceMetric } from "../../Interfaces/IEmployeePerformanceMetric"
import { ICashFlowTimelinePoint } from "../../Interfaces/ICashFlowTimelinePoint"
import { ICashFlowShareSlice } from "./ICashFlowShareSlice"

export class DailyCalculate {
  valueTotalDaily = 0
  valueTotalDailyOpen = 0
  valueTotalCanceledDaily = 0
  valueTotalGross = 0

  countOpen = 0
  countCanceled = 0
  countClosed = 0
  totalOrders = 0

  totalMeals = 0
  totalDrinks = 0
  totalAddtionals = 0
  totalExtraServices = 0
  totalDiscount = 0
  totalAwards = 0
  totalCouponDiscount = 0

  serviceOrders = 0
  deliveryOrders = 0

  totalCommissionEstimate = 0

  averageTicket = 0
  cancelRate = 0
  closureRate = 0
  openRate = 0

  hourlySales: ICashFlowTimelinePoint[] = []
  weeklySales: ICashFlowTimelinePoint[] = []

  paymentShare: ICashFlowShareSlice[] = []
  salesChannelShare: ICashFlowShareSlice[] = []
  productPerformanceShare: ICashFlowShareSlice[] = []
  employeePerformance: IEmployeePerformanceMetric[] = []

  clear() {
    this.countOpen = 0
    this.countCanceled = 0
    this.countClosed = 0
    this.totalOrders = 0

    this.valueTotalDaily = 0
    this.valueTotalDailyOpen = 0
    this.valueTotalCanceledDaily = 0
    this.valueTotalGross = 0

    this.totalMeals = 0
    this.totalDrinks = 0
    this.totalAddtionals = 0
    this.totalExtraServices = 0
    this.totalDiscount = 0
    this.totalAwards = 0
    this.totalCouponDiscount = 0

    this.serviceOrders = 0
    this.deliveryOrders = 0

    this.totalCommissionEstimate = 0

    this.averageTicket = 0
    this.cancelRate = 0
    this.closureRate = 0
    this.openRate = 0

    this.hourlySales = []
    this.weeklySales = []

    this.paymentShare = []
    this.salesChannelShare = [
      { label: "local", value: 0 },
      { label: "delivery", value: 0 },
    ]
    this.productPerformanceShare = []
    this.employeePerformance = []
  }

  calculate() {
    this.clear()

    const hourMap = new Map<string, ICashFlowTimelinePoint>()
    const dayMap = new Map<string, ICashFlowTimelinePoint>()

    const paymentMap: Record<number, number> = {
      [EPaymentType.Cash]: 0,
      [EPaymentType.Card]: 0,
      [EPaymentType.Pix]: 0,
      [EPaymentType.None]: 0,
    }
    const employeeMap = new Map<string, IEmployeePerformanceMetric>()

    orderMemory.listData.forEach((order) => {
      this.applyOrderTotals(order)

      this.applyTimeline(hourMap, this.toHourLabel(order.date), order)

      this.applyTimeline(dayMap, this.toDayLabel(order.date), order)

      this.applyPaymentShare(order, paymentMap)

      this.applySalesChannelShare(order)

      this.applyEmployeePerformance(order, employeeMap)
    })

    this.hourlySales = this.sortTimeline(hourMap)

    this.weeklySales = this.sortTimeline(dayMap)

    this.averageTicket = this.countClosed > 0 ? this.valueTotalDaily / this.countClosed : 0

    this.cancelRate = this.totalOrders > 0 ? (this.countCanceled / this.totalOrders) * 100 : 0

    this.closureRate = this.totalOrders > 0 ? (this.countClosed / this.totalOrders) * 100 : 0

    this.openRate = this.totalOrders > 0 ? (this.countOpen / this.totalOrders) * 100 : 0

    this.paymentShare = [
      { label: "cash", value: paymentMap[EPaymentType.Cash] },
      { label: "card", value: paymentMap[EPaymentType.Card] },
      { label: "pix", value: paymentMap[EPaymentType.Pix] },
      { label: "none", value: paymentMap[EPaymentType.None] },
    ]

    this.productPerformanceShare = [
      { label: "meals", value: this.totalMeals },
      { label: "drinks", value: this.totalDrinks },
      { label: "addtionals", value: this.totalAddtionals },
      { label: "services", value: this.totalExtraServices },
    ]

    this.employeePerformance = Array.from(employeeMap.values())
      .map((item) => ({
        ...item,
        averageTicket: item.closedOrdersCount > 0 ? item.netRevenue / item.closedOrdersCount : 0,
      }))
      .sort((a, b) => {
        if (b.netRevenue !== a.netRevenue) {
          return b.netRevenue - a.netRevenue
        }

        if (b.ordersCount !== a.ordersCount) {
          return b.ordersCount - a.ordersCount
        }

        return a.employeeName.localeCompare(b.employeeName)
      })
  }

  private applyOrderTotals(order: OrderEntity) {
    const payment = order.orderPaymentEntity

    const money = (payment?.totalFinal ?? 0).toString().toNumber()

    this.totalOrders += 1

    this.valueTotalGross += money

    this.totalMeals += (payment?.totalMeals ?? 0).toString().toNumber()

    this.totalDrinks += (payment?.totalDrinks ?? 0).toString().toNumber()

    this.totalAddtionals += (payment?.totalAddtionals ?? 0).toString().toNumber()

    this.totalExtraServices += (payment?.totalExtra ?? 0).toString().toNumber()

    this.totalDiscount += (payment?.totalDiscount ?? 0).toString().toNumber()

    this.totalAwards += (payment?.totalAward ?? 0).toString().toNumber()

    this.totalCouponDiscount += (payment?.totalCupom ?? 0).toString().toNumber()

    if (order.eOrderPlaceType === EOrderPlaceType.Delivery) {
      this.deliveryOrders += 1
    } else {
      this.serviceOrders += 1
    }

    this.totalCommissionEstimate += this.calculateOrderCommissionEstimate(order, money)

    if (payment?.status === ECustomerPaymentType.WithOutDebit) {
      if (order.isCancel) {
        this.countCanceled += 1

        this.valueTotalCanceledDaily += money
      } else {
        this.countClosed += 1

        this.valueTotalDaily += money
      }
      return
    }

    this.countOpen += 1
    this.valueTotalDailyOpen += money
  }

  private calculateOrderCommissionEstimate(order: OrderEntity, orderTotal: number) {
    if (order.isCancel || !order.orderPaymentEntity?.isClosed()) {
      return 0
    }

    const employeeEntityId = order.orderPaymentEntity?.employeeEntityId

    if (!employeeEntityId) {
      return 0
    }

    const employee = employeeMemory.findByStringId(employeeEntityId)

    if (!employee || employee.isMaster || employee.isDeliveryEmployee) {
      return 0
    }

    const commissionPercentage = (employee.percentCommission ?? "0").toString().toNumber()

    if (!Number.isFinite(commissionPercentage) || commissionPercentage <= 0) {
      return 0
    }

    return orderTotal * (commissionPercentage / 100)
  }

  private applyTimeline(map: Map<string, ICashFlowTimelinePoint>, label: string, order: OrderEntity) {
    const payment = order.orderPaymentEntity

    const value = (payment?.totalFinal ?? 0).toString().toNumber()

    const current =
      map.get(label) ??
      ({
        label,
        gross: 0,
        net: 0,
        canceled: 0,
        open: 0,
        orders: 0,
      } as ICashFlowTimelinePoint)

    current.gross += value

    current.orders += 1

    if (order.isCancel) {
      current.canceled += value
    } else if (payment?.status === ECustomerPaymentType.WithOutDebit) {
      current.net += value
    } else {
      current.open += value
    }

    map.set(label, current)
  }

  private applyPaymentShare(order: OrderEntity, paymentMap: Record<number, number>) {
    const paymentType = order.orderPaymentEntity?.ePaymentType ?? EPaymentType.None
    
    const normalizedPaymentType =
      paymentType === EPaymentType.Card ||
      paymentType === EPaymentType.CreditCard ||
      paymentType === EPaymentType.DebitCard ||
      paymentType === EPaymentType.FoodVoucher ||
      paymentType === EPaymentType.MealVoucher ||
      paymentType === EPaymentType.TapToPay
        ? EPaymentType.Card
        : paymentType === EPaymentType.Other
          ? EPaymentType.None
          : paymentType

    const value = (order.orderPaymentEntity?.totalFinal ?? 0).toString().toNumber()

    paymentMap[normalizedPaymentType] = (paymentMap[normalizedPaymentType] ?? 0) + value
  }

  private applySalesChannelShare(order: OrderEntity) {
    const value = (order.orderPaymentEntity?.totalFinal ?? 0).toString().toNumber()

    if (order.eOrderPlaceType === EOrderPlaceType.Delivery) {
      this.salesChannelShare[1].value += value
      return
    }

    this.salesChannelShare[0].value += value
  }

  private applyEmployeePerformance(order: OrderEntity, employeeMap: Map<string, IEmployeePerformanceMetric>) {
    const employeeEntityId = order.orderPaymentEntity?.employeeEntityId

    if (!employeeEntityId) {
      return
    }

    const employee = employeeMemory.findByStringId(employeeEntityId)

    if (!employee || employee.isDeliveryEmployee) {
      return
    }

    const current =
      employeeMap.get(employeeEntityId) ??
      ({
        employeeId: employeeEntityId,
        employeeName: employee.personEntity?.name ?? employee.userName ?? "Funcionário",
        companyFunction: employee.companyFunction ?? "",
        ordersCount: 0,
        localOrdersCount: 0,
        deliveryOrdersCount: 0,
        closedOrdersCount: 0,
        canceledOrdersCount: 0,
        grossRevenue: 0,
        netRevenue: 0,
        averageTicket: 0,
        totalItemsProduced: 0,
      } as IEmployeePerformanceMetric)

    const value = (order.orderPaymentEntity?.totalFinal ?? 0).toString().toNumber()

    const isClosed = order.orderPaymentEntity?.isClosed() && !order.isCancel

    current.ordersCount += 1

    if (order.eOrderPlaceType === EOrderPlaceType.Delivery) {
      current.deliveryOrdersCount += 1
    } else {
      current.localOrdersCount += 1
    }

    current.grossRevenue += value

    current.totalItemsProduced += order.orderDrinks.length + order.orderMeals.length + order.extraServices.length

    if (order.isCancel) {
      current.canceledOrdersCount += 1
    } else if (isClosed) {
      current.closedOrdersCount += 1
      current.netRevenue += value
    }

    employeeMap.set(employeeEntityId, current)
  }

  private sortTimeline(map: Map<string, ICashFlowTimelinePoint>) {
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label))
  }

  private toHourLabel(dateString: string) {
    return dateString.toHourLabel()
  }

  private toDayLabel(dateString: string) {
    return dateString.toDayMonthLabel()
  }
}
